import React, {useState} from 'react';
import {FilterQueryProps} from 'shared/util/encode-filters';
import {IoAdd, IoChevronDown, IoChevronUp} from 'react-icons/io5';

import {FilterOption} from './components';

import {Container, Title, Row, ToggleButton} from './styles';
import IconButton from '../icon-button';

import {
  DateFilterProps,
  FilterOptionProps,
  FilterProps,
  Operators,
} from './types';

interface Props {
  filters: FilterQueryProps;
  filterOptions: FilterOptionProps[];
  onChange: (filters: FilterQueryProps) => void;
  themeLess?: boolean;
  toggle?: boolean;
}

const FilterList: React.FC<Props> = ({
  filters = {},
  onChange,
  filterOptions,
  themeLess = false,
  toggle,
}) => {
  const [newFilter, showNewFilter] = useState(false);
  const [opened, setOpened] = useState(true);

  const availableFilters = Object.keys(filters).reduce(
    (someFilters, filterKey) => {
      const isFilterAvailable = filterOptions.find(
        (filterOption) => filterOption.field === filterKey,
      );

      if (!isFilterAvailable) return someFilters;

      return {
        ...someFilters,
        [filterKey]: filters[filterKey],
      };
    },
    {} as FilterQueryProps,
  );

  const createFilter = (
    {field, operator, value}: FilterProps,
    initialField?: string,
  ) => {
    showNewFilter(false);

    if (initialField && initialField !== field.value)
      delete filters[initialField];

    const option = filterOptions.find(
      (option) => option.field === field.value,
    )!;

    if (option.type === 'text' && option.valueAsNumber)
      return onChange({
        ...filters,
        [field.value]: {operator, value: +value},
      });

    if (option.type !== 'date')
      return onChange({
        ...filters,
        [field.value]: {operator, value},
      });

    const [startDate, endDate] = value as DateFilterProps[];

    onChange({
      ...filters,
      [field.value]: [
        {operator: Operators.GreaterThanEqual, value: startDate},
        {operator: Operators.LessThanEqual, value: endDate},
      ],
    });
  };

  const removeFilter = (field: string) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const {[field]: _, ...rest} = filters;
    onChange(rest);
  };

  const getFilter = (field: string): FilterProps => {
    const option = filterOptions.find((option) => option.field === field)!;
    const filter = availableFilters[field];

    if (!Array.isArray(filter))
      return {
        field: {label: option.label, value: option.field},
        ...filter,
      } as FilterProps;

    return {
      field: {label: option.label, value: option.field},
      operator: Operators.Equals,
      value: filter.map(({value}) => value),
    };
  };

  const activeFilters = Object.keys(availableFilters);

  return (
    <Container {...{themeLess}}>
      <Row>
        <Title>FILTROS</Title>
        {toggle && (
          <ToggleButton onClick={() => setOpened((prev) => !prev)}>
            {opened ? <IoChevronUp /> : <IoChevronDown />}
          </ToggleButton>
        )}
      </Row>
      {(opened || !toggle) && (
        <>
          {Object.keys(availableFilters).map((field) => (
            <FilterOption
              key={field}
              {...{
                confirm: createFilter,
                remove: () => removeFilter(field),
                filters: filterOptions,
                initialFilter: getFilter(field),
                activeFilters,
              }}
            />
          ))}
          {newFilter && (
            <FilterOption
              {...{
                filters: filterOptions,
                confirm: createFilter,
                remove: () => showNewFilter(false),
                activeFilters,
              }}
            />
          )}
          {Object.keys(availableFilters).length !== filterOptions.length &&
            !newFilter && (
              <IconButton
                {...{
                  variant: 'clean',
                  icon: <IoAdd />,
                  label: 'adicionar',
                  onClick: () => showNewFilter(true),
                }}
              />
            )}
        </>
      )}
    </Container>
  );
};

export default FilterList;
